"use client";
import { motion, useScroll, useSpring, useTransform } from "framer-motion";
import { ReactNode, useEffect, useRef, useState } from "react";
import AnalyticsViewportTracker from "~/components/analytics/AnalyticsViewportTracker";
import StepWithBadge from "~/components/home/StepWithBadge";
import BirdIcon from "~/components/icons/BirdIcon";
import Link from "~/components/Link";
import { AnalyticsEvents } from "~/connectors/analyticsEvents";

import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import { useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";

type Step = {
  title: string;
  description: ReactNode;
};

const steps: Step[] = [
  {
    title: "Spune-ne povestea copilului tău",
    description:
      "Nu ne da doar date. Spune-ne despre glumele lui, micile obiceiuri și momentele care vă fac să râdeți. Vor fi doar 5 minute din viața ta.",
  },
  {
    title: "Muzică creată special pentru voi",
    description:
      "Echipa noastră citește răspunsurile tale și compune o melodie unică care sună exact ca povestea copilului tău – veselă, energică, tandră, orice e nevoie să fie.",
  },
  {
    title: "Cântecul lui preferat, de acum și până târziu",
    description: (
      <>
        Descarcă piesa, adaug-o în player-ul{" "}
        <Link href="/yoto" effect="color-on-hover">
          Yoto
        </Link>
        , trimite-o celor dragi sau ascultă pe telefon, boxă smart ori prin{" "}
        <Link href="/cast" effect="color-on-hover">
          Cast
        </Link>
        , pe orice dispozitiv.
      </>
    ),
  },
];

export default function HowItWorks() {
  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const badgeRefs = useRef<Array<HTMLDivElement | null>>([]);
  const cardRefs = useRef<Array<HTMLDivElement | null>>([]);
  const [masterPath, setMasterPath] = useState<string>("");

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start 60%", "end 60%"],
  });

  const isFlyingReverse = useTransform(scrollYProgress, (current) => {
    const prev = scrollYProgress.getPrevious();
    return prev !== undefined && prev > current;
  });

  const smoothProgress = useSpring(scrollYProgress, {
    stiffness: 60,
    damping: 20,
  });
  const segments = Math.max(1, steps.length - 1);

  useEffect(() => {
    const computePath = () => {
      if (!containerRef.current) return;
      const containerRect = containerRef.current.getBoundingClientRect();
      let d = "";
      const radius = Number(theme.shape.borderRadius) || 4;
      const gap = 2; // Space between line and card

      let prevExitX = 0;
      let prevExitY = 0;

      steps.forEach((_, i) => {
        const badgeEl = badgeRefs.current[i];
        const cardEl = cardRefs.current[i];

        if (!badgeEl || !cardEl) return;

        const badge = badgeEl.getBoundingClientRect();
        const card = cardEl.getBoundingClientRect();

        const bx = badge.left + badge.width / 2 - containerRect.left;
        const by = badge.top + badge.height / 2 - containerRect.top;
        const bt = badge.top - containerRect.top;
        const bb = badge.top + badge.height - containerRect.top;

        const cw = card.width;
        const cl = card.left - containerRect.left;
        const cr = card.left + card.width - containerRect.left;
        const ct = card.top - containerRect.top;
        const cb = card.top + card.height - containerRect.top;

        const useRightSide = Math.abs(bx - cr) > Math.abs(bx - cl);

        // 1. Connect to the TOP of the badge
        if (i === 0) {
          d += `M ${bx} ${bb} `;
        } else {
          // Flowing S-Curve from previous card to this badge
          // Make the curve rounder by using dynamic control points based on distance
          const dx = bx - prevExitX;
          const dy = bt - prevExitY;
          // curvature scales with distance but clamps to reasonable values
          const curvature = Math.min(
            160,
            Math.max(40, Math.abs(dx) * 0.25 + Math.abs(dy) * 0.15),
          );
          const cp1x = prevExitX + dx * 0.25;
          const cp1y = prevExitY + curvature;
          const cp2x = bx - dx * 0.25;
          const cp2y = bt - curvature;
          d += `C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${bx} ${bt} `;
        }

        // 2. Draw straight THROUGH the badge (it will be hidden behind it!)
        d += `L ${bx} ${bt} `;

        // 3. Arc from Badge to Card Corner 1 (near corner), then trace card outline
        if (useRightSide) {
          // Badge is to the left → corner 1 = top-left, corner 2 = top-right, corner 3 = bottom-right
          d += `C ${bx} ${by + 20}, ${cl - gap} ${ct - 20}, ${cl - gap} ${ct - gap + radius} `;
          // Round corner 1 (top-left) clockwise → heading right
          d += `A ${radius} ${radius} 0 0 1 ${cl - gap + radius} ${ct - gap} `;
          // Across top to corner 2 (top-right)
          d += `L ${cr + gap - radius} ${ct - gap} `;
          // Round corner 2 (top-right) clockwise → heading down
          d += `A ${radius} ${radius} 0 0 1 ${cr + gap} ${ct - gap + radius} `;
          // Down right side to corner 3 (bottom-right)
          d += `L ${cr + gap} ${cb - radius} `;
          // Round corner 3 (bottom-right)
          // d += `A ${radius} ${radius} 0 0 1 ${cr + gap - radius} ${cb + gap} `;
          prevExitX = cr + gap - radius;
          prevExitY = cb + gap;
        } else {
          // Badge is to the right → corner 1 = top-right, corner 2 = top-left, corner 3 = bottom-left
          d += `C ${bx} ${by + 20}, ${cr + gap} ${ct - 20}, ${cr + gap} ${ct - gap + radius} `;
          // Round corner 1 (top-right) counterclockwise → heading left
          d += `A ${radius} ${radius} 0 0 0 ${cr + gap - radius} ${ct - gap} `;
          // Across top to corner 2 (top-left)
          d += `L ${cl - gap + radius} ${ct - gap} `;
          // Round corner 2 (top-left) counterclockwise → heading down
          d += `A ${radius} ${radius} 0 0 0 ${cl - gap} ${ct - gap + radius} `;
          // Down left side to corner 3 (bottom-left)
          d += `L ${cl - gap} ${cb - radius} `;
          // Round corner 3 (bottom-left)
          // d += `A ${radius} ${radius} 0 0 0 ${cl - gap + radius} ${cb + gap} `;
          prevExitX = cl - gap + radius;
          prevExitY = cb + gap;
        }
      });

      setMasterPath(d.replace(/\s+/g, " ").trim());
    };
    computePath();
    const resizeObserver = new ResizeObserver(computePath);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }
    window.addEventListener("resize", computePath);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", computePath);
    };
  }, [theme.shape.borderRadius]);

  const strokeColor = "var(--mui-palette-warning-main)";

  return (
    <AnalyticsViewportTracker eventName={AnalyticsEvents.VIEWPORT_HOW_IT_WORKS}>
      <Box sx={{ py: 8, overflow: "hidden" }}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            component="h2"
            sx={{ textAlign: "center", mb: 6 }}
          >
            Cum Funcționează
          </Typography>

          <Box
            ref={containerRef}
            sx={{
              position: "relative",
              display: "flex",
              flexDirection: "column",
              gap: 6,
              px: { xs: 2, md: 0 },
            }}
          >
            {/* Steps Rendering */}
            {steps.map((step, i) => {
              const align = i % 2 === 0 ? "left" : "right";
              return (
                <Box
                  key={i}
                  sx={{
                    display: "flex",
                    width: "100%",
                    justifyContent: {
                      xs: "center",
                      md: align === "left" ? "flex-end" : "flex-start",
                    },
                    alignItems: "center",
                    position: "relative",
                    minHeight: { xs: 120, md: 200 },
                    zIndex: 1,
                  }}
                >
                  <StepWithBadge
                    step={step}
                    idx={i}
                    align={align}
                    scrollYProgress={scrollYProgress}
                    segments={segments}
                    setRef={(el) => {
                      badgeRefs.current[i] = el;
                    }}
                    setCardRef={(el) => {
                      cardRefs.current[i] = el;
                    }}
                  />
                </Box>
              );
            })}

            {/* Continuous SVG Line */}
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                pointerEvents: "none",
              }}
            >
              <svg
                style={{ width: "100%", height: "100%", overflow: "visible" }}
              >
                <>
                  {/* Soft glow behind the stave */}
                  <motion.path
                    d={masterPath}
                    stroke={strokeColor}
                    fill="transparent"
                    strokeWidth={12}
                    strokeLinecap="round"
                    strokeOpacity={0.4}
                    style={{
                      pathLength: smoothProgress,
                      opacity: useTransform(
                        smoothProgress,
                        [0, 0.05, 0.95, 1],
                        [0, 0.25, 0.25, 0],
                      ),
                      filter: "blur(10px)",
                    }}
                  />

                  {/* Musical stave — 3 parallel lines offset vertically */}
                  <defs>
                    {([-3, 0, 3] as const).map((_, idx) => (
                      <linearGradient
                        id={`staveGradient-${idx}`}
                        key={idx}
                        gradientUnits="userSpaceOnUse"
                        x1="0%"
                        y1="0%"
                        x2="100%"
                        y2="100%"
                      >
                        <stop offset="0%" stopColor={strokeColor} stopOpacity={0} />
                        <stop offset="72%" stopColor={strokeColor} stopOpacity={0.35} />
                        <stop offset="100%" stopColor={strokeColor} stopOpacity={0.65} />
                      </linearGradient>
                    ))}
                  </defs>

                  {([-3, 0, 3] as const).map((offset, idx) => (
                    <motion.path
                      key={`${offset}-${idx}`}
                      d={masterPath}
                      stroke={`url(#staveGradient-${idx})`}
                      fill="transparent"
                      strokeWidth={1.2}
                      strokeLinecap="round"
                      strokeOpacity={1}
                      style={{
                        pathLength: smoothProgress,
                        // Shift each line slightly in SVG user-space by translating the element
                        translateY: offset,
                      }}
                    />
                  ))}
                </>
              </svg>
              <motion.div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  zIndex: 10,
                  offsetPath: `path("${masterPath}")`,
                  opacity: useTransform(
                    smoothProgress,
                    [0, 0.05, 0.95, 1],
                    [0, 1, 1, 0],
                  ),
                  offsetDistance: useTransform(
                    smoothProgress,
                    [0, 1],
                    ["0%", "100%"],
                  ),
                  offsetRotate: "auto",
                  willChange: "transform, offset-distance",
                  filter: `drop-shadow(1px 2px 4px rgba(0,0,0,0.35))`,
                  transformOrigin: "center",
                  x: -6,
                  y: 5,
                  scaleX: useTransform(isFlyingReverse, (v) => (v ? -1 : 1)),
                }}
              >
                <BirdIcon width={52} height={48} viewBox="-16 -14 32 28" fill="none" style={{ overflow: "visible" }} />
              </motion.div>
            </Box>
          </Box>
        </Container>
      </Box>
    </AnalyticsViewportTracker>
  );
}
